<script setup lang="ts">
/**
 * Товары одного шага набора — сеткой карточек, как экран «Заменить продукт» у комбо.
 *
 * Список с радио-кнопками читался хуже: у напитков и соусов названия почти одинаковые,
 * и выбирать приходилось по тексту. На карточке решает картинка, а доплата видна сразу.
 */
import type { ProductBundleStep, BundleStepProduct } from '@features/bundle';
import { isMenuItemAvailable, menuUnavailableLabel } from '@entities/product';

const props = defineProps<{
  step: ProductBundleStep;
  selectedProductId: number | null;
  formatPriceDelta: (delta: number) => string;
  placeholderImg?: string | null;
}>();

const emit = defineEmits<{
  (e: 'select', productId: number): void;
  (e: 'selectUnavailable', product: BundleStepProduct): void;
}>();

const getProductImage = (product: BundleStepProduct): string | null => {
  if (!product.img) return null;
  if (typeof product.img === 'string') return product.img;
  // Бэкенд возвращает массив [{key, url}, ...]
  if (Array.isArray(product.img)) return product.img[0]?.url ?? null;
  return null;
};

const isSelected = (product: BundleStepProduct): boolean =>
  product.product_id === props.selectedProductId;

const isAvailable = isMenuItemAvailable;
const unavailableLabel = menuUnavailableLabel;

/**
 * Недоступную карточку НЕ делаем `disabled`: браузер тогда съедает клик, и гость просто
 * тычет в мёртвую плитку. Клик принимаем, выбор не меняем и отдаём наверх — оверлей
 * объясняет тостом, почему товар взять нельзя.
 */
const handleClick = (product: BundleStepProduct) => {
  if (!isAvailable(product)) {
    emit('selectUnavailable', product);
    return;
  }
  emit('select', product.product_id);
};
</script>

<template>
  <div class="step-content" data-testid="bundle-step-content">
    <p v-if="step.subtitle" class="step-content__subtitle">{{ step.subtitle }}</p>

    <div class="step-content__grid">
      <button
        v-for="product in step.products"
        :key="product.product_id"
        type="button"
        class="step-content__card"
        :class="{
          'step-content__card--selected': isSelected(product) && isAvailable(product),
          'step-content__card--unavailable': !isAvailable(product),
        }"
        :aria-disabled="!isAvailable(product)"
        :data-testid="isAvailable(product) ? 'bundle-step-product' : 'bundle-step-product-unavailable'"
        @click="handleClick(product)"
      >
        <div class="step-content__image-wrap">
          <img
            v-if="getProductImage(product) || placeholderImg"
            :src="getProductImage(product) || placeholderImg!"
            :alt="product.name"
            class="step-content__image"
            loading="lazy"
          >
          <div v-else class="step-content__image-placeholder" />
        </div>

        <div class="step-content__name">{{ product.name }}</div>

        <!-- Доплата всегда занимает место: иначе карточки в ряду прыгают по высоте.
             У недоступного товара на её месте — причина, цена тут уже не важна. -->
        <div class="step-content__delta">
          <span v-if="!isAvailable(product)" class="step-content__badge step-content__badge--unavailable">
            {{ unavailableLabel(product) }}
          </span>
          <span
            v-else-if="product.price_delta !== 0"
            class="step-content__badge"
            :class="{ 'step-content__badge--negative': product.price_delta < 0 }"
          >
            {{ formatPriceDelta(product.price_delta) }}
          </span>
        </div>

        <!-- Галочка 1:1 как у комбо: inline SVG, lucide в проекте локально не установлен -->
        <span v-if="isSelected(product) && isAvailable(product)" class="step-content__check">
          <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.step-content {
  @apply flex flex-col gap-3;

  &__subtitle {
    @apply text-sm font-semibold text-white/70;
  }

  &__grid {
    @apply grid grid-cols-2 sm:grid-cols-3 gap-3 content-start;
  }

  // Выбранное помечаем как у комбо: белая карточка с зелёной галочкой, без рамки
  &__card {
    @apply relative rounded-2xl p-2 text-center transition-all duration-150;
    @apply bg-white/5 hover:bg-white/10;

    &--selected {
      @apply bg-white shadow-lg hover:bg-white;

      // !important обязателен: у названия есть светлый цвет для тёмной сцены,
      // на белой карточке текст сливался бы с фоном.
      .step-content__name {
        @apply text-gray-800 #{!important};
      }
    }

    // Недоступный товар: красная рамка + приглушённая картинка. Рамка внутренняя
    // (ring), иначе карточка в сетке подпрыгивает на 2px относительно соседей.
    &--unavailable {
      @apply ring-2 ring-inset ring-hui-danger bg-hui-danger/5 cursor-not-allowed;
      @apply hover:bg-hui-danger/10;

      .step-content__image-wrap,
      .step-content__name {
        @apply opacity-45;
      }
    }
  }

  &__check {
    @apply absolute top-2 right-2 w-5 h-5 rounded-full bg-hui-success text-white;
    @apply flex items-center justify-center;
  }

  &__image-wrap {
    @apply w-full rounded-xl overflow-hidden mb-2;
    aspect-ratio: 1 / 1;
  }

  &__image {
    @apply w-full h-full object-contain;
  }

  &__image-placeholder {
    @apply w-full h-full bg-gray-700;
  }

  &__name {
    @apply text-sm font-medium text-white leading-tight;
  }

  &__delta {
    @apply mt-1 min-h-[22px] flex items-center justify-center;
  }

  &__badge {
    @apply text-xs font-semibold px-2 py-0.5 rounded-full;
    @apply bg-red-500/15 text-red-500;

    &--negative {
      @apply bg-hui-success/15 text-hui-success;
    }

    // Причина недоступности читается как статус, а не как доплата: заливка плотнее
    &--unavailable {
      @apply bg-hui-danger text-hui-danger-foreground opacity-100;
    }
  }
}
</style>
